import type {
  FairCapsuleItem,
  FairCapsuleItemType,
} from '@/ui/tokens/fair-capsule.data'
import type { FairMegaMenuSize } from '@/ui/tokens/fair-mega-menu.data'
import type {
  CapsuleItem,
  CapsulesPreview,
  Item,
} from '@/types/CapsulesTypes'
import casualAccessory1 from './assets/capsules/casual-accessory-1.webp'
import casualAccessory2 from './assets/capsules/casual-accessory-2.webp'
import casualBag from './assets/capsules/casual-bag.webp'
import casualBottom from './assets/capsules/casual-bottom.webp'
import casualOuterwear from './assets/capsules/casual-outerwear.webp'
import casualShoes from './assets/capsules/casual-shoes.webp'
import casualTop from './assets/capsules/casual-top.webp'
import weekendAccessory1 from './assets/capsules/weekend-accessory-1.webp'
import weekendAccessory2 from './assets/capsules/weekend-accessory-2.webp'
import weekendBag from './assets/capsules/weekend-bag.webp'
import weekendBottom from './assets/capsules/weekend-bottom.webp'
import weekendOuterwear from './assets/capsules/weekend-outerwear.webp'
import weekendShoes from './assets/capsules/weekend-shoes.webp'
import weekendTop from './assets/capsules/weekend-top.webp'
import workdayAccessory1 from './assets/capsules/workday-accessory-1.webp'
import workdayAccessory2 from './assets/capsules/workday-accessory-2.webp'
import workdayBag from './assets/capsules/workday-bag.webp'
import workdayBottom from './assets/capsules/workday-bottom.webp'
import workdayOuterwear from './assets/capsules/workday-outerwear.webp'
import workdayShoes from './assets/capsules/workday-shoes.webp'

/** Home Capsules carousel card width — Figma `1:8735` (547px at 1920) */
export const FAIR_HOME_CAPSULES_CARD_WIDTH = 547

export const FAIR_HOME_CAPSULES_CARD_GAP = 40

export const FAIR_HOME_CAPSULES_AI_TIP_KEY = 'products_easy_to_combine' as const

export const FAIR_HOME_CAPSULES_DEFAULT_LIMIT = 10

export const FAIR_HOME_CAPSULES_DEFAULT_AUDIENCE = 'woman'

export interface FairHomeCapsuleSlide {
  id: number
  name: string
  description: string
  /** Looks inside this capsule (API `items`) */
  looks: FairCapsuleItem[][]
  /** True when slide comes from Figma demo fallback (API empty) */
  isDemo?: boolean
}

const PRODUCT_TYPE_MAP: Record<string, FairCapsuleItemType> = {
  outerwear: 'outerwear',
  top: 'top',
  bottom: 'bottom',
  bag: 'bag',
  shoes: 'shoes',
  footwear: 'shoes',
  accessory_1: 'accessory-1',
  accessory_2: 'accessory-2',
  'accessory-1': 'accessory-1',
  'accessory-2': 'accessory-2',
}

const CASUAL_ITEMS: FairCapsuleItem[] = [
  { id: 'c-outerwear', type: 'outerwear', imageUrl: casualOuterwear, alt: 'Outerwear' },
  { id: 'c-bottom', type: 'bottom', imageUrl: casualBottom, alt: 'Bottom' },
  { id: 'c-top', type: 'top', imageUrl: casualTop, alt: 'Top' },
  { id: 'c-acc-1', type: 'accessory-1', imageUrl: casualAccessory1, alt: 'Sunglasses' },
  { id: 'c-acc-2', type: 'accessory-2', imageUrl: casualAccessory2, alt: 'Earrings' },
  { id: 'c-bag', type: 'bag', imageUrl: casualBag, alt: 'Bag' },
  { id: 'c-shoes', type: 'shoes', imageUrl: casualShoes, alt: 'Shoes' },
]

const WORKDAY_ITEMS: FairCapsuleItem[] = [
  { id: 'w-outerwear', type: 'outerwear', imageUrl: workdayOuterwear, alt: 'Coat' },
  { id: 'w-bottom', type: 'bottom', imageUrl: workdayBottom, alt: 'Dress' },
  { id: 'w-acc-1', type: 'accessory-1', imageUrl: workdayAccessory1, alt: 'Earrings' },
  { id: 'w-acc-2', type: 'accessory-2', imageUrl: workdayAccessory2, alt: 'Glasses' },
  { id: 'w-bag', type: 'bag', imageUrl: workdayBag, alt: 'Bag' },
  { id: 'w-shoes', type: 'shoes', imageUrl: workdayShoes, alt: 'Heels' },
]

const WEEKEND_ITEMS: FairCapsuleItem[] = [
  { id: 'e-outerwear', type: 'outerwear', imageUrl: weekendOuterwear, alt: 'Jacket' },
  { id: 'e-bottom', type: 'bottom', imageUrl: weekendBottom, alt: 'Jeans' },
  { id: 'e-top', type: 'top', imageUrl: weekendTop, alt: 'Sweater' },
  { id: 'e-acc-1', type: 'accessory-1', imageUrl: weekendAccessory1, alt: 'Cap' },
  { id: 'e-acc-2', type: 'accessory-2', imageUrl: weekendAccessory2, alt: 'Necklace' },
  { id: 'e-bag', type: 'bag', imageUrl: weekendBag, alt: 'Backpack' },
  { id: 'e-shoes', type: 'shoes', imageUrl: weekendShoes, alt: 'Sneakers' },
]

/** Figma demo slides — used only when API returns empty */
export const FAIR_HOME_CAPSULES_DEMO_SLIDES: FairHomeCapsuleSlide[] = [
  {
    id: -1,
    name: 'Casual Comfort Capsule',
    description: 'Relaxed essentials for everyday wear, blending style and ease.',
    looks: [CASUAL_ITEMS],
    isDemo: true,
  },
  {
    id: -2,
    name: 'Workday Essentials Capsule',
    description: 'Polished pieces to power through the week with confidence.',
    looks: [WORKDAY_ITEMS],
    isDemo: true,
  },
  {
    id: -3,
    name: 'Effortless Weekend Capsule',
    description: 'Chill styles for brunches, strolls, and laid-back plans.',
    looks: [WEEKEND_ITEMS],
    isDemo: true,
  },
  {
    id: -4,
    name: 'Casual Comfort Capsule',
    description: 'Relaxed essentials for everyday wear, blending style and ease.',
    looks: [CASUAL_ITEMS],
    isDemo: true,
  },
]

function getLookProducts(look: Item): CapsuleItem[] {
  return look.capsuleItems ?? look.capsule_items ?? []
}

function getProductImageUrl(product: CapsuleItem): string {
  return product.imageUrl ?? product.image_url ?? ''
}

function getProductId(product: CapsuleItem): number {
  return product.productId ?? product.product_id ?? 0
}

function getProductType(product: CapsuleItem): string {
  return (product.productType ?? product.product_type ?? '').toLowerCase()
}

function mapApiProductToFairItem(
  product: CapsuleItem,
): FairCapsuleItem | null {
  const type = PRODUCT_TYPE_MAP[getProductType(product)]
  const imageUrl = getProductImageUrl(product)
  if (!type || !imageUrl) {
    return null
  }

  const id = getProductId(product)
  const name = product.productName ?? product.product_name ?? ''
  const brand = product.brandName ?? product.brand_name ?? ''
  const variantId =
    product.productVariantId ?? product.product_variant_id ?? undefined

  return {
    id,
    type,
    imageUrl,
    alt: name || type,
    product: {
      id,
      name,
      brand,
      price: product.price ?? 0,
      variantId,
    },
  }
}

function mapLookToFairItems(look: Item): FairCapsuleItem[] {
  const byType = new Map<FairCapsuleItemType, FairCapsuleItem>()

  for (const product of getLookProducts(look)) {
    const mapped = mapApiProductToFairItem(product)
    if (!mapped) continue
    if (!byType.has(mapped.type)) {
      byType.set(mapped.type, mapped)
    }
  }

  return [...byType.values()]
}

/** Map API `CapsulesPreview[]` → Home carousel slides */
export function mapCapsulesPreviewToFairSlides(
  capsules: CapsulesPreview[] | null | undefined,
): FairHomeCapsuleSlide[] {
  if (!capsules?.length) {
    return []
  }

  return capsules
    .map((capsule) => {
      const looks = (capsule.items ?? [])
        .map(mapLookToFairItems)
        .filter((items) => items.length > 0)

      return {
        id: capsule.id,
        name: capsule.name,
        description: capsule.description,
        looks,
      }
    })
    .filter((slide) => slide.looks.length > 0)
}

/**
 * Prefer API slides; fall back to Figma demo when backend returns [].
 * Remove demo fallback once capsules API has data in all envs.
 */
export function resolveFairHomeCapsuleSlides(
  capsules: CapsulesPreview[] | null | undefined,
  options?: { allowDemoFallback?: boolean },
): FairHomeCapsuleSlide[] {
  const fromApi = mapCapsulesPreviewToFairSlides(capsules)
  if (fromApi.length) return fromApi
  if (options?.allowDemoFallback !== false) {
    return FAIR_HOME_CAPSULES_DEMO_SLIDES
  }
  return []
}

export function getFairHomeCapsuleLookItems(
  slide: FairHomeCapsuleSlide,
  lookIndex: number,
): FairCapsuleItem[] {
  if (!slide.looks.length) return []
  const safe =
    ((lookIndex % slide.looks.length) + slide.looks.length) %
    slide.looks.length
  return slide.looks[safe] ?? []
}

export function getFairHomeCapsulesMegaMenuSize(width: number): FairMegaMenuSize {
  if (width < 768) return '393'
  if (width < 1024) return '768'
  if (width < 1366) return '1366'
  // Keep 1440 tokens until true 1920 room — 80px gaps crush cards under ~1600.
  if (width < 1600) return '1440'
  return '1920'
}

export function getFairHomeCapsulesCardWidth(width: number): number {
  if (width < 768) return 268
  if (width < 1024) return 390
  return FAIR_HOME_CAPSULES_CARD_WIDTH
}

export function getFairHomeCapsulesPadInline(width: number): number {
  if (width < 768) return 16
  if (width < 1024) return 40
  if (width < 1440) return 60
  return 100
}

export function getFairHomeCapsulesCardsPerView(
  width: number,
  cardWidth: number,
  gap = FAIR_HOME_CAPSULES_CARD_GAP,
): number {
  if (width < 768) return 1
  if (width < 1024) return 2
  const available = width - getFairHomeCapsulesPadInline(width) * 2
  const fit = Math.floor((available + gap) / (cardWidth + gap))
  return Math.max(1, Math.min(fit, 3))
}

export function getFairHomeCapsulesCarouselPageCount(
  totalSlides: number,
  cardsPerView: number,
): number {
  if (totalSlides <= 0) return 1
  if (totalSlides <= cardsPerView) return 1
  return totalSlides - cardsPerView + 1
}

export function clampFairHomeCapsulesCarouselPage(
  page: number,
  totalPages: number,
): number {
  if (totalPages < 1) return 1
  const safe = Math.floor(page)
  if (safe < 1) return 1
  if (safe > totalPages) return totalPages
  return safe
}

export function getFairHomeCapsulesCarouselOffset(
  page: number,
  cardWidth: number,
  gap = FAIR_HOME_CAPSULES_CARD_GAP,
): number {
  return (page - 1) * (cardWidth + gap)
}
